import { __assign } from "tslib";
export var createHeaders = function (headerSettings) {
    return __assign({ 'Content-Type': 'text/plain' }, (typeof headerSettings === 'function'
        ? headerSettings()
        : headerSettings));
};
// --- Default values ---
var DEFAULT_STATUS_CODE_OVERRIDES = {
    '408': 'retry',
    '410': 'retry',
    '429': 'retry',
    '460': 'retry',
    '501': 'drop',
    '505': 'drop',
    '511': 'drop',
};
/** Clamp a number to a range, returning the default if the value is undefined. */
function clamp(value, defaultValue, min, max) {
    var v = value !== null && value !== void 0 ? value : defaultValue;
    return Math.min(Math.max(v, min), max);
}
/**
 * Parse the Retry-After header from a response, if present and applicable.
 * Returns `{ retryAfterMs, fromHeader }` when a valid delay is found, or `null` otherwise.
 */
export function parseRetryAfter(res, rateLimitConfig) {
    var _a;
    if (res.status !== 429) {
        return null;
    }
    var raw = (_a = res.headers) === null || _a === void 0 ? void 0 : _a.get('Retry-After');
    if (!raw) {
        return null;
    }
    var parsed = parseInt(raw, 10);
    if (Number.isNaN(parsed)) {
        return null;
    }
    var cappedSeconds = Math.max(0, Math.min(parsed, rateLimitConfig.maxRetryInterval));
    return { retryAfterMs: cappedSeconds * 1000, fromHeader: true };
}
/**
 * Determine whether a given HTTP status code should cause a retry or a drop,
 * based on the resolved backoff configuration.
 */
export function getStatusBehavior(status, backoffConfig) {
    var override = backoffConfig.statusCodeOverrides[String(status)];
    if (override) {
        return override;
    }
    if (status >= 500)
        return backoffConfig.default5xxBehavior;
    if (status >= 400)
        return backoffConfig.default4xxBehavior;
    return 'drop';
}
/**
 * Compute an exponential backoff delay in milliseconds for the given attempt.
 * Attempt is 1-based (first retry = 1).
 */
export function computeBackoff(attempt, config) {
    var baseMs = config.baseBackoffInterval * 1000;
    var maxMs = config.maxBackoffInterval * 1000;
    var exponential = baseMs * Math.pow(2, attempt - 1);
    var capped = Math.min(exponential, maxMs);
    var jitter = 1 + (Math.random() - 0.5) * 2 * (config.jitterPercent / 100);
    return Math.max(0, capped * jitter);
}
/**
 * Resolve an optional HttpConfig from CDN/user settings into a fully-populated
 * config object with defaults applied and values clamped to safe ranges.
 */
export function resolveHttpConfig(config, cdnConfig) {
    var _a, _b, _c, _d;
    // Merge order and precedence:
    // 1) `config` is the init-time base.
    // 2) `cdnConfig` is applied second and wins on overlapping fields.
    //    The CDN will only be populated as an override when necessary to address
    //    issues so takes precedence over init config to ensure it can do so effectively.
    // 3) `statusCodeOverrides` is deep-merged so CDN can override specific
    //    init-provided codes without replacing the whole map.
    // This keeps precedence centralized here instead of repeating merge logic
    // in each caller.
    var mergedConfig = config || cdnConfig
        ? {
            rateLimitConfig: __assign(__assign({}, config === null || config === void 0 ? void 0 : config.rateLimitConfig), cdnConfig === null || cdnConfig === void 0 ? void 0 : cdnConfig.rateLimitConfig),
            backoffConfig: __assign(__assign(__assign({}, config === null || config === void 0 ? void 0 : config.backoffConfig), cdnConfig === null || cdnConfig === void 0 ? void 0 : cdnConfig.backoffConfig), { statusCodeOverrides: __assign(__assign({}, (_a = config === null || config === void 0 ? void 0 : config.backoffConfig) === null || _a === void 0 ? void 0 : _a.statusCodeOverrides), (_b = cdnConfig === null || cdnConfig === void 0 ? void 0 : cdnConfig.backoffConfig) === null || _b === void 0 ? void 0 : _b.statusCodeOverrides) }),
        }
        : undefined;
    var rate = mergedConfig === null || mergedConfig === void 0 ? void 0 : mergedConfig.rateLimitConfig;
    var backoff = mergedConfig === null || mergedConfig === void 0 ? void 0 : mergedConfig.backoffConfig;
    return {
        rateLimitConfig: {
            maxRetryCount: clamp(rate === null || rate === void 0 ? void 0 : rate.maxRetryCount, 10, 0, 100),
            maxRetryInterval: clamp(rate === null || rate === void 0 ? void 0 : rate.maxRetryInterval, 300, 0.1, 86400),
            maxRateLimitDuration: clamp(rate === null || rate === void 0 ? void 0 : rate.maxRateLimitDuration, 43200, 10, 86400),
        },
        backoffConfig: {
            maxRetryCount: clamp(backoff === null || backoff === void 0 ? void 0 : backoff.maxRetryCount, 10, 0, 100),
            baseBackoffInterval: clamp(backoff === null || backoff === void 0 ? void 0 : backoff.baseBackoffInterval, 0.5, 0.1, 300),
            maxBackoffInterval: clamp(backoff === null || backoff === void 0 ? void 0 : backoff.maxBackoffInterval, 60, 0.1, 86400),
            maxTotalBackoffDuration: clamp(backoff === null || backoff === void 0 ? void 0 : backoff.maxTotalBackoffDuration, 43200, 60, 604800),
            jitterPercent: clamp(backoff === null || backoff === void 0 ? void 0 : backoff.jitterPercent, 10, 0, 100),
            default4xxBehavior: (_c = backoff === null || backoff === void 0 ? void 0 : backoff.default4xxBehavior) !== null && _c !== void 0 ? _c : 'drop',
            default5xxBehavior: (_d = backoff === null || backoff === void 0 ? void 0 : backoff.default5xxBehavior) !== null && _d !== void 0 ? _d : 'retry',
            statusCodeOverrides: __assign(__assign({}, DEFAULT_STATUS_CODE_OVERRIDES), backoff === null || backoff === void 0 ? void 0 : backoff.statusCodeOverrides),
        },
    };
}
//# sourceMappingURL=shared-dispatcher.js.map